import { trim } from "lodash";
import React from "react";
import { useCallback, useMemo } from "react";
import { updateIssueByIdAction } from "../../../../exports/redux";
import {
  Issue,
  IssueCustomAttributeDefinition,
  IssueCustomAttributeMapping,
} from "@adsk/acc-issues-types";
import {
  CustomFieldInForm,
  useCustomAttributeBasedOnAttributeId,
  getCustomAttributeDisplayValue,
} from "../../../input-field-components/EditFormCustomField";
import { useDispatch } from "@adsk/acc-issues-core/state/redux/hooks";
import { trackIssueCustomFieldEdit } from "../../../utils/analytics/issueActionsTracking";
import { isAttributeEditable } from "../../../utils/data/entityPermissions";
import { IssueChange } from "./typesForFields";

const useIssueCustomAttribute = ({
  issue,
  attributeDefinitionId,
  onUpdate,
  onUpdateFailed,
}: {
  issue: Issue;
  attributeDefinitionId: string;
  onUpdate: (change: IssueChange) => void;
  onUpdateFailed: () => void;
}) => {
  const attribute: IssueCustomAttributeDefinition =
    useCustomAttributeBasedOnAttributeId(attributeDefinitionId);

  const issueAttribute = issue.customAttributes.find(
    attr => attr.attributeDefinitionId === attributeDefinitionId
  );

  const value = issueAttribute && issueAttribute.value;
  const displayValue = useMemo(
    () => getCustomAttributeDisplayValue(attribute, value),
    [value, attribute]
  );

  const dispatch = useDispatch();
  const handleCustomAttributeChange = useCallback(
    (newValue: any) => {
      const value = typeof newValue === "string" ? trim(newValue) : newValue;
      const issueChanges = {
        customAttributes: [{ attributeDefinitionId, value }],
      };
      trackIssueCustomFieldEdit({
        issueUid: issue.id,
        issueAttribute,
        newValue: getCustomAttributeDisplayValue(attribute, value),
        oldValue: getCustomAttributeDisplayValue(
          attribute,
          issueAttribute && issueAttribute.value
        ),
      });
      dispatch(
        updateIssueByIdAction({
          id: issue.id,
          issueChanges,
          onSuccess: () => onUpdate(issueChanges),
          onFailure: onUpdateFailed,
        })
      );
    },
    [
      attribute,
      attributeDefinitionId,
      dispatch,
      issue.id,
      issueAttribute,
      onUpdate,
      onUpdateFailed,
    ]
  );

  return {
    handleCustomAttributeChange,
    attribute,
    // The actual custom attribute value
    value,
    // The value to be displayed.
    // If the custom attribute is a dropdown, this is the label.
    displayValue,
  };
};

export const IssueEditFormCustomField = ({
  issue,
  attributeDefinitionId,
  required = false,
  containerRef,
  onUpdate,
  onUpdateFailed,
  viewOnly = false,
}: {
  issue: Issue;
  containerRef?: React.MutableRefObject<null>;
  onUpdate: (change: IssueChange) => void;
  onUpdateFailed: () => void;
  required?: boolean;
  viewOnly?: boolean;
} & IssueCustomAttributeMapping) => {
  const { attribute, value, displayValue, handleCustomAttributeChange } =
    useIssueCustomAttribute({
      issue,
      attributeDefinitionId,
      onUpdate,
      onUpdateFailed,
    });

  const disabled = viewOnly || !isAttributeEditable(issue, "customAttributes");

  return (
    <CustomFieldInForm
      attribute={attribute}
      value={value}
      displayValue={displayValue}
      disabled={disabled}
      isRequired={required}
      containerRef={containerRef}
      handleCustomAttributeChange={handleCustomAttributeChange}
      viewOnly={viewOnly}
    />
  );
};
